import React, { FC, useCallback, useMemo } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { MIN_USD_PRICE } from 'constants/constants';
import {
  GeneralSection,
  SegmentsSection,
  TagsSection
} from 'views/Offers/components/sections';

import FormSectionsTabs from 'components/FormSectionsTabs';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { FormControl, FormField, FormItem } from 'components/ui';
import { Separator } from 'components/ui/separator';
import { Tabs, TabsContent } from 'components/ui/tabs';

import {
  getPriorityOptions,
  PopupsFormFields,
  PRIORITY_NONE_VALUE
} from '../PopupsForm.consts';

import { ProductsSection } from './Sections/ProductsSection';

interface IStandardPopupsContentProps {
  form: UseFormReturn<PopupsFormFields>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  offerDesignOptions: { externalId: string; value: string; label: string }[];
  isNameTaken: boolean;
  hasFeatureFlagTags: boolean;
  getProducts: any;
  productsList: any[];
  addProduct: (product: any) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: any) => void;
  formatProductQuantity: any;
}

const StandardPopupsContent: FC<IStandardPopupsContentProps> = ({
  form,
  activeTab,
  setActiveTab,
  offerDesignOptions,
  isNameTaken,
  hasFeatureFlagTags,
  getProducts,
  productsList,
  addProduct,
  deleteProduct,
  updateProduct,
  formatProductQuantity
}): React.ReactElement => {
  const handleSegmentsChange = useCallback(
    (segments: string[]): void => {
      form.setValue('segments', segments, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const handleTagsChange = useCallback(
    (newTags: string[]): void => {
      form.setValue('tags', newTags, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const priceConfig = useMemo(() => ({
    disabled: true,
    type: 'text' as const,
    displayValue: 'Free',
    min: MIN_USD_PRICE
  }), []);

  const priorityOptions = useMemo(() => getPriorityOptions(), []);

  const renderPriorityField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: { value: number | null; onChange: (...event: unknown[]) => void };
    }): React.ReactElement => {
      const dropdownValue = value === null ? PRIORITY_NONE_VALUE : value;
      const selectedPriority = priorityOptions.find(
        (option) => option.value === dropdownValue
      );
      const handlePriorityChange = (newValue: string | number): void => {
        onChange(newValue === PRIORITY_NONE_VALUE ? null : newValue);
      };
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={String(dropdownValue)}
              label="Priority"
              placeholder="Enter Number"
              defaultSelected={selectedPriority}
              options={priorityOptions}
              onOptionChange={handlePriorityChange}
              listDataTestId="dashPopupPriorityList"
              buttonDataTestId="popupPriorityBox"
            />
          </FormControl>
        </FormItem>
      );
    },
    [priorityOptions]
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <FormSectionsTabs
        currentTab={activeTab}
        tabs={[
          { value: 'settings', label: 'Settings' },
          { value: 'configuration', label: 'Configuration' }
        ]}
      />
      <TabsContent
        value="settings"
        forceMount
        className="mt-9 flex flex-col gap-y-9 pb-10 data-[state=inactive]:hidden"
      >
        <GeneralSection
          offerDesignOptions={offerDesignOptions}
          isNameTaken={isNameTaken}
          priceConfig={priceConfig}
          offerDesignListDataTestId="dashPopupDesignList"
          offerDesignButtonDataTestId="popupDesignBox"
        >
          <div className="grid grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="priority"
              render={renderPriorityField}
            />
          </div>
        </GeneralSection>
      </TabsContent>
      <TabsContent
        value="configuration"
        forceMount
        className="mt-9 flex flex-col gap-y-5 data-[state=inactive]:hidden"
      >
        <SegmentsSection
          items={form.watch('segments') || []}
          onItemsChange={handleSegmentsChange}
          listTestId="popupSegmentsList"
        />
        {hasFeatureFlagTags && (
          <React.Fragment>
            <Separator className="bg-base-border" />
            <TagsSection
              items={form.watch('tags') || []}
              onItemsChange={handleTagsChange}
            />
          </React.Fragment>
        )}
        <Separator className="bg-base-border" />
        <ProductsSection
          form={form}
          isDailyBonus
          formatProductQuantity={formatProductQuantity}
          products={getProducts}
          productsList={productsList}
          addProduct={addProduct}
          deleteProduct={deleteProduct}
          updateProduct={updateProduct}
        />
      </TabsContent>
    </Tabs>
  );
};

export { StandardPopupsContent };
